import React, { useEffect, useMemo, useState } from 'react';
import { useSidebar } from '@/components/ui/sidebar';
import { Logo } from '@/components/logos/logo';
import {
  ConnectionStatus,
  ConnectionStatusV2,
  IntegrationTypeV2,
  IntegrationV2Properties,
  Merchant
} from '@/gql/graphql';
import { getStatusIndicatorColor, readableStatus, LOGO_MAPPING } from '../tracking-utils';
import styles from './integration-diagram.module.css';
import { BASE_SCREEN_WIDTH, MEDIUM_SCREEN_WIDTH, SMALL_SCREEN_WIDTH } from './constants';
import animationData from '../../../../public/MeshAnimation.json';
import { LottieResponsive } from '@/components/lottie-responsive';
import { cn } from '@/lib/utils';

interface Item {
  name: string;
  status: ConnectionStatusV2;
}

type HoverContentProps = {
  items: Item[];
  className?: string;
};

interface IntegrationDiagramProps {
  merchantInfo: Merchant | undefined;
  destinations: IntegrationV2Properties[] | undefined;
}

export const IntegrationDiagram = ({ merchantInfo, destinations }: IntegrationDiagramProps) => {
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < MEDIUM_SCREEN_WIDTH);
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);

  // Debug logging
  console.log('IntegrationDiagram Debug:', {
    merchantInfo,
    destinations,
    windowWidth,
    isMobile
  });

  const currentIntegrations = useMemo(() => destinations ?? [], [destinations]);
  const hasIssue = useMemo(() => {
    return currentIntegrations.some((item) => item.status === ConnectionStatusV2.Error);
  }, [currentIntegrations]);

  const hasConnected = useMemo(() => {
    return currentIntegrations.some((item) => item.status === ConnectionStatusV2.Connected);
  }, [currentIntegrations]);

  const groupedIntegrations = useMemo(() => {
    const result = currentIntegrations.reduce<Record<string, IntegrationV2Properties[]>>(
      (acc, item) => {
        acc[item.status] = acc[item.status] ?? [];
        acc[item.status].push(item);
        return acc;
      },
      {}
    );
    console.log('groupedIntegrations:', result);
    return result;
  }, [currentIntegrations]);

  const { open: isSidebarOpen } = useSidebar();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < SMALL_SCREEN_WIDTH);
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const logoSize = isMobile ? 35 : 50;
  const maxNodesVisible = useMemo(() => {
    const result =
      windowWidth < MEDIUM_SCREEN_WIDTH || (windowWidth <= BASE_SCREEN_WIDTH && isSidebarOpen)
        ? 0
        : 4;
    console.log('maxNodesVisible calculation:', {
      windowWidth,
      MEDIUM_SCREEN_WIDTH,
      BASE_SCREEN_WIDTH,
      isSidebarOpen,
      result
    });
    return result;
  }, [isSidebarOpen, windowWidth]);

  const HoverContent = ({ items }: HoverContentProps) => (
    <div
      className={cn(
        'absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-50',
        'w-max bg-white text-integrationBannerForeground border shadow-lg rounded-lg px-2 py-1 hidden group-hover:block transition-all duration-300'
      )}
    >
      {items.map((item: Item, index: number) => (
        <div key={index} className="flex justify-between items-center w-full gap-4 z-50">
          <div className="flex items-center gap-2">
            <span className="text-integrationBannerForeground">{item.name}</span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="flex font-mono font-medium">{readableStatus(item.status)}</span>
            <div
              className={cn('w-2.5 h-2.5 rounded-[2px]', getStatusIndicatorColor(item.status))}
            />
          </div>
        </div>
      ))}
    </div>
  );

  type ConnectionStatusString = `${ConnectionStatus}`;
  const IntegrationNodesByStatusDisplay = ({
    status,
    items
  }: {
    status: ConnectionStatusString;
    items: IntegrationV2Properties[];
  }) => {
    const visibleItems = items.slice(0, maxNodesVisible);
    const restItems = items.slice(maxNodesVisible);
    const borderStyle =
      // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
      status === ConnectionStatus.Connected ? 'border-teal-400' : 'border-amber-500';

    const getZIndex = (index: number, length: number, status: string) => {
      const baseZIndex = status === 'CONNECTED' ? 20 : 100;
      return baseZIndex + (length - index);
    };

    return (
      <React.Fragment>
        {' '}
        {visibleItems.map((item, index) => (
          <div
            key={index}
            style={{ zIndex: getZIndex(index, visibleItems.length, status) }}
            className={`${styles.nodeStyle} ${styles[`status-${status}`]} ${borderStyle} rounded-[12px] border-2 bg-white group h-10 w-[40px] z-5`}
          >
            {item.type !== IntegrationTypeV2.Unspecified && (
              <Logo icon={LOGO_MAPPING[item.type]} height={18} width={20} />
            )}
            <HoverContent items={[{ name: item.name, status: item.status }]} />
          </div>
        ))}
        {restItems.length > 0 && (
          <div
            className={`${styles.nodeStyle} ${styles[`status-${status}`]} ${borderStyle} rounded-[12px] border-2 text-integrationBannerForeground bg-white group h-10 w-[40px]`}
            style={{ zIndex: getZIndex(5, 0, status) }}
          >
            +{restItems.length}
            <HoverContent items={restItems.map((i) => ({ name: i.name, status: i.status }))} />
          </div>
        )}
      </React.Fragment>
    );
  };

  const ShopifyNode = () => {
    return (
      <div className="w-[152px] h-10 py-0 px-0 group rounded-[12px] border-2 border-teal-400 bg-white cursor-pointer relative">
        <div className="relative py-2 px-4 flex items-center justify-evenly h-full">
          <div className="w-[32px] h-[32px] p-[4px]">
            <div className="w-full h-full rounded-full overflow-hidden">
              <Logo height={24} width={24} icon="shopifyBannerLogo" />
            </div>
          </div>
          <div className="hidden sm:block text-integrationBannerForeground text-sm font-medium text-center vertical-center">
            {merchantInfo?.websiteName ?? 'Shopify'}
          </div>
          <HoverContent
            items={[
              { name: merchantInfo?.websiteName ?? 'Shopify', status: ConnectionStatusV2.Connected }
            ]}
            className="left-10 top-3"
          />
        </div>
      </div>
    );
  };

  const ShopifyToContextualEdge = () => {
    return <div className={`${styles.borderStyle} sm:w-[80px] w-[60px] h-[2px]`} />;
  };

  const ContextualToIntegrationGroupEdges = ({
    hasIssue,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    hasConnected
  }: {
    hasIssue: boolean;
    hasConnected: boolean;
  }) => {
    return hasIssue ? (
      <div className="w-[80px]">
        <svg viewBox="0 0 500 250" width="100%" height="100%" preserveAspectRatio="none">
          <path
            d="M0 125 C 324 100, 166 -50, 1000 0"
            className={styles.connectedStatusBorder}
            fill="transparent"
          />
          <path
            d="M0 125 C 166 100, 332 230, 500 250"
            className={styles.issueStatusBorder}
            fill="transparent"
          />
        </svg>
      </div>
    ) : (
      <div className={`${styles.borderStyle} sm:w-[80px] w-[60px] h-[2px]`} />
    );
  };

  const ContextualNode = () => {
    return (
      <div className="rounded-100 h-[100px] w-[100px] rounded-[25px] border-2 border-teal-400 bg-white">
        <div className="h-full w-full flex items-center justify-center">
          <Logo icon="ctxSymbol" height={logoSize} width={logoSize} />
        </div>
      </div>
    );
  };

  return (
    <div className="rounded-lg relative overflow-hidden w-full h-[226px] min-h-[226px]">
      <LottieResponsive
        animationData={animationData}
        height={226}
        className="absolute top-0 left-0 w-full z-0"
      />
      <div className="w-full flex justify-center items-center relative z-10">
        <div className="h-[226px] flex justify-between items-center mr-8">
          <ShopifyNode />
          <ShopifyToContextualEdge />
          <ContextualNode />
          {(hasConnected || hasIssue) && (
            <ContextualToIntegrationGroupEdges hasIssue={hasIssue} hasConnected={hasConnected} />
          )}

          {/* Integration Nodes Container */}
          <div className={styles.nodeContainerStyle}>
            {/* Debug: Show all integrations regardless of status */}
            {Object.entries(groupedIntegrations).length === 0 && (
              <div className="text-red-500 text-sm">No integrations found</div>
            )}
            {/* Loop over statuses and render nodes for each using the internal component */}
            {Object.entries(groupedIntegrations)
              .filter(([status]) => status !== 'DISCONNECTED')
              .map(([status, items]) => (
                <IntegrationNodesByStatusDisplay
                  key={status}
                  status={status as ConnectionStatusString}
                  items={items}
                />
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};
